/**
 * 地理位置帮助函数
 */

import Storage from '@medlinker/storage';
import env from './env';

// export const LBS_KEY = 'V7LBZ-724KW-WJPRR-R5Z6E-63FMV-MTFK4';
// export const LBS_KEY = 'IOVBZ-V6GWL-VUZP4-MC4UF-TQIBO-ERFB7';
// export const LBS_KEY = 'LXPBZ-AYIEH-NMXDP-WRQPW-MI3N7-YDFKF';
export const LBS_KEY = 'HKGBZ-L3OWB-IJ6UK-NPZGA-R6RLH-NGBZL';
export const LBS_REFERER = 'e-mall';

export interface Provice extends Region {
  cidx: [number, number];
  pinyin: [string, string];
}

export interface City extends Region {
  cidx?: [number, number];
  pinyin: [string, string];
}

export interface Region {
  fullname: string;
  id: string;
  location: { lat: number; lnt: number };
}

const storage = new Storage({
  namespace: 'ih-lbs',
});

/**
 * 获取行政区域列表
 */
export const getDistrictList = async (): Promise<
  [Provice[], City[], Region[]]
> => {
  let data = storage.load({ key: 'district_list' });

  if (!data) {
    const res = await jsonp<{
      status: number;
      message: string;
      result: [Provice[], City[], Region[]];
    }>(
      `https://apis.map.qq.com/ws/district/v1/list?output=jsonp&key=${LBS_KEY}`
    );

    if (res.status !== 0) {
      throw new Error(`获取行政区域失败: ${res.message}`);
    }

    data = res.result;
    storage.save({ key: 'district_list', value: data });
  }

  return data;
};

/**
 * 通过ip获取地址
 */
export const getAddressByIP = async (level: 'city' | 'province' = 'city') => {
  const res = await jsonp<{
    status: number;
    message: string;
    result: {
      ip: string;
      location: {
        lat: number;
        lng: number;
      };
      ad_info: {
        nation: string;
        province: string;
        city: string;
        district: string;
        adcode: string;
      };
    };
  }>(`https://apis.map.qq.com/ws/location/v1/ip?output=jsonp&key=${LBS_KEY}`);

  if (res.status !== 0) {
    throw new Error(`地址定位失败: ${res.message}`);
  }

  if (level === 'city' && !res.result.ad_info.city) {
    throw new Error(`城市定位失败，请打开定位或者wifi重试`);
  }

  return res.result;
};

/**
 * 获取用户定位（使用精准定位，默认失败可回退到IP定位）
 */
export function getUserLocation({
  /** 精准定位超时时间 */
  timeout = 2000,
  /** 精准定位失败是否可回退到IP定位 */
  fallback = true,
  /** IP定位超时时间 */
  fallbackTimeout = 1000,
} = {}): Promise<{
  module: 'geolocation';
  /** 定位类型: h5 | ip | cache */
  type: string;
  /** 行政ID */
  adcode: string;
  /** 国家 */
  nation: string;
  /** 省 */
  province: string;
  /** 城市 */
  city: string;
  /** 区域 */
  district: string;
  /** 地址 */
  addr: string;
  /** 纬度 */
  lat: number;
  /** 经度 */
  lng: number;
  /** 误差范围，以米为单位 */
  accuray: number;
}> {
  // 针对小程序内嵌的，无法验证qq地图域名，先屏蔽小程序定位
  // 2021-11-12 未来医生线上体验出现，所以屏蔽
  if (env.isWechatMiniProgram) {
    return new Promise((resolve, reject) => {
      // eslint-disable-next-line no-restricted-properties
      // System.import(
      //   'https://mapapi.qq.com/web/mapComponents/geoLocation/v/geolocation.min.js'
      // ).then(() => {
      //   const geo = new (window as any).qq.maps.Geolocation(
      //     LBS_KEY,
      //     LBS_REFERER
      //   );

      //   geo.getLocation((res: any) => {
      //     resolve(res);
      //   }, reject);
      // });

      let location: any;

      navigator.geolocation.getCurrentPosition(
        res => {
          jsonp(
            `https://apis.map.qq.com/ws/coord/v1/translate?key=${LBS_KEY}&locations=${res.coords.latitude},${res.coords.longitude}&type=1&output=jsonp`
          )
            .then(res => {
              location = res.locations[0];

              return jsonp(
                `https://apis.map.qq.com/ws/geocoder/v1/?location=${location.lat},${location.lng}&key=${LBS_KEY}&output=jsonp`
              );
            })
            .then(res => {
              if (res?.status !== 0) {
                reject(new Error('获取地址失败'));
              }

              resolve({
                lat: location.lat,
                lng: location.lng,
                addr: res.result.address,
                adcode: res.result.adcode,
                ...res.result.address_component,
              });
            })
            .catch(reject);
        },
        err => {
          console.error('定位失败', err);
          reject(err);
        },
        {
          enableHighAccuracy: true,
          timeout: 50000,
          maximumAge: 3000,
        }
      );
    });
  }

  return new Promise((resolve, reject) => {
    const iframe = document.createElement('iframe');

    iframe.setAttribute('id', 'qq-lbs-geolocation');
    iframe.src = `https://apis.map.qq.com/tools/geolocation?key=${LBS_KEY}&referer=${LBS_REFERER}`;
    iframe.setAttribute('allow', 'geolocation');
    iframe.style.display = 'none';

    let location: any = null;
    let timeoutTimer: any = null;

    const onMessage = (event: any) => {
      const { data } = event;

      if (data && data.module === 'geolocation') {
        location = data;
        document.body.removeChild(iframe);
        window.removeEventListener('message', onMessage);
        if (timeoutTimer) {
          clearTimeout(timeoutTimer);
          timeoutTimer = null;
        }

        resolve(location);
      }
    };

    timeoutTimer = setTimeout(() => {
      if (!location) {
        if (fallback) {
          if (iframe.contentWindow) {
            iframe.contentWindow.postMessage('getLocation.robust', '*');
          }

          timeoutTimer = setTimeout(() => {
            if (!location) {
              reject(new Error('location is undefined'));
            }
          }, fallbackTimeout);
        } else {
          reject(new Error('fallback is false'));
        }
      }
    }, timeout);

    window.addEventListener('message', onMessage, false);

    document.body.appendChild(iframe);
  });
}

/**
 * 经纬度解析地理位置
 */
export const geocoder = async (lat: number, lng: number) => {
  const res = await jsonp<{
    status: number;
    message: string;
    result: {
      address: string;
      location: {
        lat: number;
        lng: number;
      };
      ad_info: {
        nation_code: string;
        nation: string;
        province: string;
        city: string;
        district: string;
        adcode: string;
        city_code: string;
      };
      formatted_addresses: {
        recommend: string;
      };
      address_component: {
        province: string;
        city: string;
        district: string;
        street: string;
        street_number: string;
      };
    };
  }>(
    `https://apis.map.qq.com/ws/geocoder/v1/?location=${lat},${lng}&output=jsonp&key=${LBS_KEY}`
  );

  if (res.status !== 0) {
    throw new Error(`获取位置失败: ${res.message}`);
  }

  return res.result;
};

interface LatLng {
  lat: number;
  lng: number;
}
interface DistanceElement {
  from: LatLng;
  to: LatLng;
  distance: number;
  duration: number;
}

/**
 * 根据经纬度计算距离（一对多）
 */
async function getDistance(options: {
  mode?: 'driving' | 'walking';
  /** 起点：单一坐标 */
  from: LatLng;
  /** 终点：单一坐标 | 多坐标  */
  to: LatLng;
}): Promise<DistanceElement>;
async function getDistance(options: {
  mode?: 'driving' | 'walking';
  /** 起点：单一坐标 */
  from: LatLng;
  /** 终点：单一坐标 | 多坐标  */
  to: LatLng[];
}): Promise<DistanceElement[]>;
async function getDistance(options: any) {
  const { mode = 'driving', from, to } = options;

  const formattedFrom = `${from.lat},${from.lng}`;
  const formattedTo = (Array.isArray(to) ? to : [to])
    .map(item => `${item.lat},${item.lng}`)
    .join(';');

  const res = await jsonp<{
    status: number;
    message: string;
    result: {
      elements: DistanceElement[];
    };
  }>(
    `https://apis.map.qq.com/ws/distance/v1/?output=jsonp&mode=${mode}&from=${formattedFrom}&to=${formattedTo}&key=${LBS_KEY}`
  );

  if (res.status !== 0) {
    throw new Error(`位置计算失败: ${res.message}`);
  }

  return Array.isArray(to) ? res.result.elements : res.result.elements[0];
}
export { getDistance };

export interface SearchAddressResultItem {
  id: string;
  title: string;
  address: string;
  province: string;
  city: string;
  district: string;
  location: {
    lat: number;
    lng: number;
  };
  adcode: number;
}

/**
 * 关键字搜索地址
 */
export const searchAddress = async (region: string, keyword: string) => {
  const res = await jsonp<{
    status: number;
    message: string;
    count: number;
    data: SearchAddressResultItem[];
  }>(
    `https://apis.map.qq.com/ws/place/v1/suggestion?region_fix=1&policy=1&keyword=${keyword}&region=${region}&policy=1&page_size=15&output=jsonp&key=${LBS_KEY}`
  );

  if (res.status !== 0) {
    throw new Error(`地址定位失败: ${res.message}`);
  }

  return { data: res.data, count: res.count };
};

export class JSONPError extends Error {}

export const jsonp = <T = any>(url: string) =>
  new Promise<T>((resolve, reject) => {
    const script = document.createElement('script');
    const contactor = url.includes('?') ? '&' : '?';
    const cbName = `jsonp_${Math.floor(Math.random() * 1000000)}`;

    const clear = () => {
      // @ts-ignore
      delete window[cbName];
      document.body.removeChild(script);
    };

    // @ts-ignore
    window[cbName] = (res: T) => {
      resolve(res);
      clear();
    };

    script.onerror = () => {
      reject(new JSONPError('jsonp调用失败'));
      clear();
    };

    script.src = `${url}${contactor}callback=${cbName}`;

    document.body.appendChild(script);
  });
